<template>
  <b-modal
    id="csv-import-modal"
    v-model="showModal"
    title="Importer des Colis via CSV"
    size="xl"
    hide-footer
    no-close-on-backdrop
    @hidden="resetForm"
  >
    <div class="csv-import-container">
      <!-- Step Indicator -->
      <div class="steps-indicator mb-3">
        <div
          class="step"
          :class="{ active: currentStep === 1, completed: currentStep > 1 }"
        >
          <div class="step-number">
            1
          </div>
          <div class="step-label">
            Télécharger / Uploader
          </div>
        </div>
        <div class="step-line" />
        <div
          class="step"
          :class="{ active: currentStep === 2, completed: currentStep > 2 }"
        >
          <div class="step-number">
            2
          </div>
          <div class="step-label">
            Mapper les Colonnes
          </div>
        </div>
        <div class="step-line" />
        <div
          class="step"
          :class="{ active: currentStep === 3 }"
        >
          <div class="step-number">
            3
          </div>
          <div class="step-label">
            Aperçu et Import
          </div>
        </div>
      </div>

      <!-- Step 1: Upload CSV -->
      <div v-if="currentStep === 1">
        <b-alert
          show
          variant="info"
          class="mb-3"
        >
          <div class="d-flex align-items-start">
            <feather-icon
              icon="InfoIcon"
              size="20"
              class="mr-1"
            />
            <div>
              <h6 class="mb-1">
                Comment importer vos colis ?
              </h6>
              <ul class="mb-0 pl-3">
                <li>Téléchargez le modèle CSV pour voir le format attendu</li>
                <li>Ou uploadez votre propre fichier CSV et mappez les colonnes</li>
                <li>Assurez-vous que les données sont correctes avant l'import</li>
              </ul>
            </div>
          </div>
        </b-alert>

        <!-- Governorate Spelling Warning -->
        <b-alert
          show
          variant="light"
          class="mb-3 custom-governorate-warning bg-transparent border-0 p-0"
        >
          <div class="d-flex align-items-start">
            <div class="warning-icon-wrapper mr-2">
              <feather-icon
                icon="AlertTriangleIcon"
                size="20"
                class="text-danger"
              />
            </div>
            <div>
              <h6 class="mb-1 text-black">
                <strong>Note : Pour assurer le succès de l'import, respectez strictement l'orthographe des gouvernorats comme suit :</strong>
              </h6>
              <div class="row text-black">
                <div class="col-md-3">
                  <ul class="mb-0 pl-3 small">
                    <li>Ariana</li>
                    <li>Béja</li>
                    <li>Ben Arous</li>
                    <li>Bizerte</li>
                    <li>Gabès</li>
                    <li>Gafsa</li>
                  </ul>
                </div>
                <div class="col-md-3">
                  <ul class="mb-0 pl-3 small">
                    <li>Jendouba</li>
                    <li>Kairouan</li>
                    <li>Kasserine</li>
                    <li>Kébili</li>
                    <li>Kef</li>
                    <li>Mahdia</li>
                  </ul>
                </div>
                <div class="col-md-3">
                  <ul class="mb-0 pl-3 small">
                    <li>Manouba</li>
                    <li>Médenine</li>
                    <li>Monastir</li>
                    <li>Nabeul</li>
                    <li>Sfax</li>
                    <li>Sidi Bouzid</li>
                  </ul>
                </div>
                <div class="col-md-3">
                  <ul class="mb-0 pl-3 small">
                    <li>Siliana</li>
                    <li>Sousse</li>
                    <li>Tataouine</li>
                    <li>Tozeur</li>
                    <li>Tunis</li>
                    <li>Zaghouan</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </b-alert>

        <!-- Download Template Button -->
        <div class="text-center mb-3 p-3 border rounded bg-light">
          <feather-icon
            icon="DownloadIcon"
            size="48"
            class="text-primary mb-2"
          />
          <h5 class="mb-2">
            Télécharger le Modèle CSV
          </h5>
          <p class="text-muted mb-2">
            Utilisez notre modèle pour un import rapide et sans erreur
          </p>
          <b-button
            variant="primary"
            @click="downloadTemplate"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            Télécharger le Modèle
          </b-button>
        </div>

        <div class="text-center my-3">
          <span class="text-muted">- OU -</span>
        </div>

        <!-- Upload CSV File -->
        <div class="upload-zone">
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            class="d-none"
            @change="handleFileUpload"
          >
          <div
            class="upload-dropzone"
            :class="{ 'drag-over': isDragging }"
            @click="$refs.fileInput.click()"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
          >
            <feather-icon
              icon="UploadCloudIcon"
              size="48"
              class="text-muted mb-2"
            />
            <h5 class="mb-1">
              Cliquez ou Glissez votre fichier CSV ici
            </h5>
            <p class="text-muted mb-0">
              Formats acceptés: .csv
            </p>
            <p
              v-if="uploadedFile"
              class="mt-2 text-success"
            >
              <feather-icon
                icon="CheckIcon"
                size="16"
              />
              Fichier sélectionné: <strong>{{ uploadedFile.name }}</strong>
            </p>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            @click="closeModal"
          >
            Annuler
          </b-button>
          <b-button
            variant="primary"
            :disabled="!uploadedFile"
            class="ml-2"
            @click="processFile"
          >
            Continuer
            <feather-icon
              icon="ArrowRightIcon"
              class="ml-50"
            />
          </b-button>
        </div>
      </div>

      <!-- Step 2: Map Columns -->
      <div v-if="currentStep === 2">
        <b-alert
          show
          variant="warning"
          class="mb-3"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="18"
            class="mr-1"
          />
          <strong>Mappez vos colonnes CSV</strong> aux champs de l'application
        </b-alert>

        <div class="mapping-container">
          <b-table
            :items="csvHeaders"
            :fields="mappingFields"
            responsive
            striped
            hover
          >
            <template #cell(csvColumn)="data">
              <strong>{{ data.item }}</strong>
              <div class="text-muted small">
                Exemple: {{ getColumnPreview(data.item) }}
              </div>
            </template>

            <template #cell(appField)="data">
              <v-select
                v-model="columnMapping[data.item]"
                :options="availableFields"
                :reduce="field => field.value"
                label="label"
                placeholder="Sélectionner un champ"
                :clearable="true"
              >
                <template #option="option">
                  <div>
                    <strong>{{ option.label }}</strong>
                    <div class="text-muted small">
                      {{ option.description }}
                    </div>
                  </div>
                </template>
              </v-select>
            </template>
          </b-table>
        </div>

        <div class="d-flex justify-content-between mt-3">
          <b-button
            variant="secondary"
            @click="currentStep = 1"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              class="mr-50"
            />
            Retour
          </b-button>
          <b-button
            variant="primary"
            @click="validateMapping"
          >
            Continuer
            <feather-icon
              icon="ArrowRightIcon"
              class="ml-50"
            />
          </b-button>
        </div>
      </div>

      <!-- Step 3: Preview and Import -->
      <div v-if="currentStep === 3">
        <b-alert
          show
          variant="success"
          class="mb-3"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="18"
            class="mr-1"
          />
          <strong>{{ validRows.length }} colis</strong> prêts à être importés
          <span
            v-if="invalidRows.length > 0"
            class="ml-2 text-danger"
          >
            ({{ invalidRows.length }} lignes avec erreurs)
          </span>
        </b-alert>

        <!-- Preview Table -->
        <div class="preview-container">
          <h6 class="mb-2">
            Aperçu des données (5 premières lignes)
          </h6>
          <b-table
            :items="previewData"
            :fields="previewFields"
            responsive
            striped
            small
            class="preview-table"
          />
        </div>

        <!-- Invalid Rows -->
        <div
          v-if="invalidRows.length > 0"
          class="mt-3"
        >
          <b-card
            header="Lignes avec Erreurs"
            header-bg-variant="danger"
            header-text-variant="white"
          >
            <b-table
              :items="invalidRows"
              :fields="errorFields"
              responsive
              striped
              small
            >
              <template #cell(errors)="data">
                <b-badge
                  v-for="(error, idx) in data.value"
                  :key="idx"
                  variant="danger"
                  class="mr-1"
                >
                  {{ error }}
                </b-badge>
              </template>
            </b-table>
          </b-card>
        </div>

        <div class="d-flex justify-content-between mt-3">
          <b-button
            variant="secondary"
            @click="currentStep = 2"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              class="mr-50"
            />
            Retour
          </b-button>
          <div>
            <b-button
              variant="primary"
              :disabled="importing || validRows.length === 0"
              @click="importOrders"
            >
              <b-spinner
                v-if="importing"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="CheckIcon"
                class="mr-50"
              />
              {{ importing ? 'Import en cours...' : `Importer ${validRows.length} Colis` }}
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BAlert, BTable, BCard, BBadge, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'

export default {
  name: 'CsvImportModal',
  components: {
    BModal,
    BButton,
    BAlert,
    BTable,
    BCard,
    BBadge,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      showModal: false,
      currentStep: 1,
      uploadedFile: null,
      isDragging: false,
      csvData: [],
      csvHeaders: [],
      columnMapping: {},
      validRows: [],
      invalidRows: [],
      importing: false,
      mappingFields: [
        { key: 'csvColumn', label: 'Colonne CSV' },
        { key: 'appField', label: 'Champ Application' },
      ],
      availableFields: [
        { value: 'delivery_name', label: 'Nom du Destinataire', description: 'Obligatoire' },
        { value: 'delivery_phone', label: 'Téléphone', description: 'Obligatoire - 8 chiffres' },
        { value: 'delivery_phone2', label: 'Téléphone 2', description: 'Optionnel' },
        { value: 'delivery_address', label: 'Adresse', description: 'Obligatoire' },
        { value: 'governorate', label: 'Gouvernorat', description: 'Obligatoire' },
        { value: 'delegation', label: 'Délégation', description: 'Obligatoire' },
        { value: 'locality', label: 'Localité', description: 'Optionnel' },
        { value: 'product_name', label: 'Nom du Produit', description: 'Obligatoire' },
        { value: 'price', label: 'Prix (DT)', description: 'Obligatoire - Nombre' },
        { value: 'quantity', label: 'Quantité', description: 'Optionnel - Défaut: 1' },
        { value: 'note', label: 'Note', description: 'Optionnel' },
        { value: 'delivery_option', label: 'Option Livraison', description: 'domicile/stopdesk' },
      ],
      errorFields: [
        { key: 'row', label: 'Ligne' },
        { key: 'data', label: 'Données' },
        { key: 'errors', label: 'Erreurs' },
      ],
    }
  },
  computed: {
    previewData() {
      return this.validRows.slice(0, 5)
    },
    previewFields() {
      const fields = []
      Object.keys(this.columnMapping).forEach(csvCol => {
        const appField = this.columnMapping[csvCol]
        if (appField) {
          const fieldDef = this.availableFields.find(f => f.value === appField)
          fields.push({
            key: appField,
            label: fieldDef ? fieldDef.label : appField,
          })
        }
      })
      return fields
    },
  },
  methods: {
    openModal() {
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    resetForm() {
      this.currentStep = 1
      this.uploadedFile = null
      this.csvData = []
      this.csvHeaders = []
      this.columnMapping = {}
      this.validRows = []
      this.invalidRows = []
    },
    downloadTemplate() {
      const template = [
        ['delivery_name', 'delivery_phone', 'delivery_address', 'governorate', 'delegation', 'locality', 'product_name', 'price', 'quantity', 'note', 'delivery_option'],
        ['Ahmed Ben Ali', '12345678', '10 Rue de la Liberté', 'Tunis', 'La Marsa', 'Sidi Daoud', 'Produit Test', '50.500', '1', 'Appeler avant livraison', 'domicile'],
        ['Fatma Trabelsi', '98765432', '25 Avenue Habib Bourguiba', 'Ariana', 'Ariana Ville', '', 'Produit 2', '75.000', '2', '', 'stopdesk'],
      ]

      const csv = template.map(row => row.join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'modele_import_colis.csv'
      link.click()
    },
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.uploadedFile = file
      }
    },
    handleDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && file.name.endsWith('.csv')) {
        this.uploadedFile = file
      } else {
        this.$toast.error('Veuillez sélectionner un fichier CSV')
      }
    },
    processFile() {
      if (!this.uploadedFile) return

      const reader = new FileReader()

      reader.onload = e => {
        try {
          const text = e.target.result
          const lines = text.split('\n').filter(line => line.trim())

          if (lines.length < 2) {
            this.$toast.error('Le fichier CSV doit contenir au moins une ligne d\'en-tête et une ligne de données')
            return
          }

          // Parse headers
          this.csvHeaders = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''))

          // Parse data rows
          this.csvData = []
          for (let i = 1; i < lines.length; i += 1) {
            const values = this.parseCSVLine(lines[i])
            const row = {}
            this.csvHeaders.forEach((header, index) => {
              row[header] = values[index] || ''
            })
            this.csvData.push(row)
          }

          // Auto-map columns if they match
          this.autoMapColumns()

          this.currentStep = 2
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('CSV Parse Error:', error)
          this.$toast.error('Erreur lors de la lecture du fichier CSV')
        }
      }

      reader.onerror = () => {
        this.$toast.error('Erreur lors de la lecture du fichier')
      }

      reader.readAsText(this.uploadedFile)
    },
    parseCSVLine(line) {
      const values = []
      let current = ''
      let inQuotes = false

      for (let i = 0; i < line.length; i += 1) {
        const char = line[i]
        const nextChar = line[i + 1]
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"'
            i += 1
          } else {
            inQuotes = !inQuotes
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }

      values.push(current.trim())
      return values
    },
    autoMapColumns() {
      this.csvHeaders.forEach(header => {
        const normalizedHeader = header.toLowerCase().trim()
        const match = this.availableFields.find(field => field.value === normalizedHeader || field.label.toLowerCase() === normalizedHeader)

        if (match) {
          this.$set(this.columnMapping, header, match.value)
        }
      })
    },
    getColumnPreview(column) {
      if (this.csvData.length > 0) {
        return this.csvData[0][column] || '-'
      }
      return '-'
    },
    validateMapping() {
      // Check required fields are mapped
      const requiredFields = ['delivery_name', 'delivery_phone', 'delivery_address', 'governorate', 'delegation', 'product_name', 'price']
      const mappedValues = Object.values(this.columnMapping)

      const missingFields = requiredFields.filter(field => !mappedValues.includes(field))

      if (missingFields.length > 0) {
        this.$toast.error(`Veuillez mapper les champs obligatoires: ${missingFields.join(', ')}`)
        return
      }

      // Process and validate data
      this.processData()
      this.currentStep = 3
    },
    processData() {
      this.validRows = []
      this.invalidRows = []

      this.csvData.forEach((row, index) => {
        const mappedRow = {}
        const errors = []

        Object.keys(this.columnMapping).forEach(csvCol => {
          const appField = this.columnMapping[csvCol]
          if (appField) {
            mappedRow[appField] = row[csvCol]
          }
        })

        // Validate required fields
        if (!mappedRow.delivery_name) errors.push('Nom manquant')
        if (!mappedRow.delivery_phone) errors.push('Téléphone manquant')
        if (mappedRow.delivery_phone && !/^[24579]\d{7}$/.test(mappedRow.delivery_phone)) {
          errors.push('Ce numéro de téléphone n\'est pas un numéro réel. Veuillez saisir un numéro de téléphone valide.')
        }
        if (mappedRow.delivery_phone && /^(\d)\1{7}$/.test(mappedRow.delivery_phone)) {
          errors.push('Ce numéro de téléphone n\'est pas un numéro réel. Veuillez saisir un numéro de téléphone valide.')
        }
        if (!mappedRow.delivery_address) errors.push('Adresse manquante')
        if (!mappedRow.governorate) errors.push('Gouvernorat manquant')
        if (!mappedRow.delegation) errors.push('Délégation manquante')
        if (!mappedRow.product_name) errors.push('Produit manquant')
        if (!mappedRow.price || Number.isNaN(parseFloat(mappedRow.price))) {
          errors.push('Prix invalide')
        }

        if (errors.length === 0) {
          this.validRows.push(mappedRow)
        } else {
          this.invalidRows.push({
            row: index + 2, // +2 because index starts at 0 and header is row 1
            data: `${JSON.stringify(mappedRow).substring(0, 50)}...`,
            errors,
          })
        }
      })
    },
    async importOrders() {
      this.importing = true
      try {
        const response = await axios.post('/api/orders/bulk-create/', {
          orders: this.validRows,
        })

        this.$toast.success(`${response.data.created_count || this.validRows.length} colis importés avec succès`)
        this.$emit('imported', response.data)
        this.closeModal()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Import Error:', error)
        this.$toast.error('Erreur lors de l\'import des colis')
      } finally {
        this.importing = false
      }
    },
  },
}
</script>

<style scoped>
.csv-import-container {
  padding: 24px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 16px;
}

.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 35px;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.step-number {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.step.active .step-number {
  background: rgba(17, 64, 146, 0.1);
  color: #FF0D02;
  box-shadow: 0 6px 20px rgba(17, 64, 146, 0.08);
  transform: scale(1.05);
  border: 1px solid rgba(17, 64, 146, 0.2);
}

.step.completed .step-number {
  background: linear-gradient(135deg, #28c76f 0%, #1ea35d 100%);
  color: white;
  box-shadow: 0 6px 20px rgba(40, 199, 111, 0.4);
}

.step-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  transition: all 0.3s ease;
}

.step.active .step-label {
  color: #111827;
  font-weight: 700;
}

.step.completed .step-label {
  color: #28c76f;
  font-weight: 600;
}

.step-line {
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, #e5e7eb 0%, #d1d5db 100%);
  margin: 0 15px;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.step.completed + .step-line,
.step-line:has(+ .step.completed),
.step.active + .step-line {
  background: linear-gradient(90deg, rgba(17, 64, 146, 0.1) 0%, rgba(17, 64, 146, 0.1) 100%);
}

.upload-dropzone {
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  padding: 60px 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  position: relative;
  overflow: hidden;
}

.upload-dropzone::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(17, 64, 146, 0.1) 0%, rgba(17, 64, 146, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.upload-dropzone:hover::before,
.upload-dropzone.drag-over::before {
  opacity: 1;
}

.upload-dropzone:hover,
.upload-dropzone.drag-over {
  border-color: rgba(17, 64, 146, 0.1);
  box-shadow: 0 10px 40px rgba(17, 64, 146, 0.1);
  transform: translateY(-2px);
}

.mapping-container {
  max-height: 400px;
  overflow-y: auto;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.preview-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.preview-table {
  font-size: 13px;
}

/* Custom alert styling */
.csv-import-container :deep(.alert-info) {
  background-color: rgba(17, 64, 146, 0.12) !important;
  border: none;
  border-radius: 12px;
  color: #000000 !important;
}

.csv-import-container :deep(.alert-info) .feather {
  color: #FF0D02 !important;
}

.csv-import-container :deep(.alert-warning) {
  background: linear-gradient(135deg, rgba(17, 64, 146, 0.05) 0%, rgba(17, 64, 146, 0.05) 100%);
  border: none;
  border-left: 4px solid rgba(17, 64, 146, 0.1);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(17, 64, 146, 0.1);
}

/* Download template section */
.csv-import-container .border.rounded.bg-light {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.csv-import-container .border.rounded.bg-light:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

/* Primary button styling */
.csv-import-container :deep(.btn-primary) {
  background: linear-gradient(135deg, #FF0D02 0%, #FF0D02 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(17, 64, 146, 0.1) !important;
  transition: all 0.3s ease !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  padding: 10px 24px !important;
}

.csv-import-container :deep(.btn-primary:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(17, 64, 146, 0.1) !important;
  background: linear-gradient(135deg, #FF0D02 0%, #FF0D02 100%) !important;
}

/* Text primary color override */
.csv-import-container :deep(.text-primary) {
  color: #FF0D02 !important;
}
.warning-icon-wrapper {
  background-color: rgba(17, 64, 146, 0.1);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
}

.text-black {
  color: #000000 !important;
}
</style>
